import { combineReducers, configureStore, Middleware } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE, persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import api from '../app/api';
import appReducer from './app-slice';
import authReducer from './auth-slice';
import toastReducer from './toast-slice';
import workspaceReducer from './workspace-slice';

const reducers = combineReducers({
    api: api.reducer,
    app: appReducer,
    auth: authReducer,
    workspace: workspaceReducer,
    toast: toastReducer,
});

const persistConfig = {
    key: 'root',
    storage,
    whitelist: ['auth', 'workspace'],
};

const persistedReducer = persistReducer(persistConfig, reducers);

const roleChangeMiddleware: Middleware = (store) => (next) => (action) => {
    const prevRole = store.getState().auth.role;
    const result = next(action);
    const newRole = store.getState().auth.role;

    if (prevRole !== newRole) {
        store.dispatch(api.util.resetApiState());
    }
    return result;
};

const workspaceChangeMiddleware: Middleware = (store) => (next) => (action) => {
    const prevPlaceId = store.getState().workspace.placeId;
    const result = next(action);
    const newPlaceId = store.getState().workspace.placeId;

    if (prevPlaceId !== newPlaceId) {
        store.dispatch(api.util.resetApiState());
    }
    return result;
};

export const setupStore = (preloadedState?) =>
    configureStore({
        // replace for combine reducers
        reducer: persistedReducer,
        // devTools: process.env.NODE_ENV !== "production",
        middleware: (getDefaultMiddleware) => {
            const middlewares = getDefaultMiddleware({
                serializableCheck: {
                    ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
                },
            }).concat(api.middleware, roleChangeMiddleware, workspaceChangeMiddleware);
            return middlewares;
        },
        preloadedState,
    });

export const store = setupStore();

const persistor = persistStore(store);

setupListeners(store.dispatch);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export { persistor };
